import React, { Fragment, useCallback, useEffect, useRef, useState, } from 'react';
import { Button, Form, Input, Popconfirm, Result, Steps } from 'antd';
import { SyncOutlined, WarningOutlined } from '@ant-design/icons';
import colorsDark from '../../../assets/colors/dark';
import { useTranslation } from 'react-i18next';
import * as SERVICE from '../../../constants/service.types';
import { useDispatch, useSelector } from 'react-redux';
import { socketEmitEvent } from '../../../redux/actions';
import Thumbnail from '../../../assets/images/thumbnail.jpg';
import regex from '../../../constants/system.keys';
import InputTag from '../../../components/InputTag';
import LocationSearchLocationIQ from '../../../components/LocationSearchLocationIQ';

const { Step } = Steps;

const ScanAddCameraComponent = ({ onDone, gatewaySerial, gatewayAddress }) => {
  /**
   * @param loading
   * @type 0:off, 1:on, -1: false
   */
  const [autoScan, setAutoScan] = useState({
    loading: 0,
    data: [],
  });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [autoTimer, setAutoTimer] = useState();
  const refAutoTimer = useRef();
  const [requestScan, setRequestScan] = useState();
  const [updateReturn, setUpdateReturn] = useState();
  const {
    scanStreamSource,
    checkStreamSource,
    addExternalDevice,
    updateDevice,
    namespace,
  } = useSelector((state) => state);
  const [isBlock, setIsBlock] = useState(false);
  const [address, setAddress] = useState({
    note: '',
    lat: '',
    lng: '',
  });
  const [form] = Form.useForm();
  const [, forceUpdate] = useState();
  useEffect(() => {
    forceUpdate({});
  }, []);

  useEffect(() => {
    return () => {
      setAutoScan({
        loading: 0,
        data: [],
      });
      setAutoTimer(null);
      setRequestScan(null);
      setUpdateReturn(null);
      setIsBlock(false);
      setAddress({
        note: '',
        lat: '',
        lng: '',
      });
    };
  }, []);

  // Start: Scan

  const handleOnScan = useCallback(() => {
    setAutoTimer(10);
    setAutoScan((state) => ({ ...state, loading: 1 }));
    let currentTime = new Date().getTime();

    setRequestScan({
      step: 0,
      requestId: currentTime,
    });

    refAutoTimer.current = setInterval(() => {
      setAutoTimer((timer) => timer - 1);
    }, 1000);
    let data = {
      namespace,
      gatewaySerial,
    };

    let dataObj = {
      appServiceType: SERVICE.SCAN_NEW_CAMERA,
      requestId: currentTime,
      body: JSON.stringify(data),
    };

    return dispatch(socketEmitEvent(dataObj));
  }, [dispatch, gatewaySerial, namespace]);

  useEffect(() => {
    if (autoTimer === 0) {
      setAutoScan({ data: [], loading: -1 });
      clearInterval(refAutoTimer.current);
    }
  }, [autoTimer]);

  useEffect(() => {
    if (
      requestScan &&
      scanStreamSource &&
      requestScan.step === 0 &&
      scanStreamSource.result === 1 &&
      scanStreamSource.requestId === requestScan.requestId
    ) {
      clearInterval(refAutoTimer.current);
      setAutoScan({
        loading: 0,
        data: scanStreamSource.data.map((device) => ({
          ...device,
          loading: false,
          isAdded: false,
        })),
      });
    }
    return () => { };
  }, [scanStreamSource, requestScan]);
  // End: Scan

  // Start: Check source
  const handleAuthenticationSource = useCallback(
    (values) => {
      const { source, username, password, serial } = values;
      setAutoScan((state) => ({
        ...state,
        data: state.data.map((device) =>
          device.serial !== serial ? device : { ...device, loading: true }
        ),
      }));
      let currentTime = new Date().getTime();
      setRequestScan({
        step: 1,
        requestId: currentTime,
      });
      let data = {
        source,
        serial,
        username,
        password,
        namespace,
        gatewaySerial,
      };
      let dataObj = {
        appServiceType: SERVICE.REQUEST_CHECK_STREAM,
        requestId: currentTime,
        body: JSON.stringify(data),
      };

      return dispatch(socketEmitEvent(dataObj));
    },
    [dispatch, gatewaySerial, namespace]
  );

  useEffect(() => {
    if (
      requestScan &&
      checkStreamSource &&
      requestScan.step === 1 &&
      checkStreamSource.result === 1 &&
      checkStreamSource.requestId === requestScan.requestId
    ) {
      const { serial, authenticationRtsp } = checkStreamSource.data;
      if (authenticationRtsp === 'require') {
        form.setFields([
          {
            name: 'password',
            value: '',
            errors: [t('Username of password is incorrect!')],
          },
        ]);
        setAutoScan((state) => ({
          ...state,
          data: state.data.map((device) =>
            device.serial !== serial
              ? device
              : {
                ...device,
                loading: false,
              }
          ),
        }));
      } else {
        setAutoScan((state) => ({
          ...state,
          data: state.data?.filter(item => item.serial === serial).map((device) => ({
            ...device,
            authenticationRtsp,
            loading: false,
          })
          ),
        }));
      }
    }
    return () => { };
  }, [checkStreamSource, form, requestScan, t]);

  // End: Check source

  // Start: Add camera
  const handleAddExternalDevice = useCallback(
    (serial, device) => {
      let data = {
        serial,
        namespace,
        extraInfo: {
          ...device
        }
      };
      let currentTime = new Date().getTime();

      setRequestScan({
        step: null,
        requestId: currentTime,
      });
      let dataObj = {
        appServiceType: SERVICE.REQUEST_ADD_EXTERNAL_DEVICE,
        requestId: currentTime,
        body: JSON.stringify(data),
      };
      setAutoScan((state) => ({
        ...state,
        data: state.data.map((device) =>
          device.serial !== serial
            ? device
            : {
              ...device,
              loading: true,
            }
        ),
      }));
      return dispatch(socketEmitEvent(dataObj));
    },
    [dispatch, namespace]
  );

  useEffect(() => {
    if (addExternalDevice) {
      setAutoScan((state) => ({
        ...state,
        data: state.data?.filter(item => item.serial === addExternalDevice.serial).map((device) => ({
          ...device,
          isAdded: true,
          loading: false,
        })
        ),
      }));
    }
  }, [addExternalDevice]);

  // End: Add camera

  const handleFinishAdd = useCallback(
    (serial) => {
      form.resetFields();
      setRequestScan(null);
      onDone();
    },
    [form, onDone]
  );

  // Start: Update camera

  const handleAddressChange = (address) => {
    form.setFieldsValue({
      location: address,
    });
    setAddress((state) => ({
      ...state,
      note: address,
    }));
  };

  useEffect(() => {
    if (gatewayAddress) {
      form.setFieldsValue({
        location: gatewayAddress?.note ?? '',
      });
      setAddress(gatewayAddress);
    }
  }, [gatewayAddress]);

  const handleSelect = (address) => {
    form.setFieldsValue({
      location: address?.note ?? '',
    });
    setAddress(address);
  };

  const handleUpdateDevice = useCallback(
    (values) => {
      const { serial, name, tags } = values;
      const { note, lat, lng } = address;
      if (serial) {
        let data = {
          note: note ?? "",
          lat: lat ?? 0,
          lng: lng ?? 0,
          serial,
          name: name ? name : serial,
          namespace,
          tags,
        };
        let currentTime = new Date().getTime();

        setRequestScan({
          step: null,
          requestId: currentTime,
        });
        let dataObj = {
          appServiceType: SERVICE.UPDATE_DEVICE,
          requestId: currentTime,
          body: JSON.stringify(data),
        };

        setAutoScan((state) => ({
          ...state,
          data: state.data.map((device) =>
            device.serial === serial ? { ...device, loading: true } : device
          ),
        }));

        return dispatch(socketEmitEvent(dataObj));
      }
    },
    [address, dispatch, namespace]
  );

  useEffect(() => {
    if (
      updateDevice &&
      requestScan &&
      updateDevice.requestId === requestScan.requestId
    ) {
      setUpdateReturn(updateDevice);
    }
  }, [requestScan, updateDevice]);
  useEffect(() => {
    if (updateReturn && updateReturn.serial) {
      if (updateReturn.isSuccess) {
        handleFinishAdd(updateReturn.serial);
      } else {
        setAutoScan((state) => ({
          ...state,
          data: state.data.map((device) =>
            device.serial !== updateReturn.serial
              ? device
              : { ...device, loading: false }
          ),
        }));
      }
    }
  }, [handleFinishAdd, t, updateReturn]);

  // End: Update camera
  return (
    <div className="scan-add-camera-component">
      {autoScan && autoScan.data.length > 0 ? (
        <Fragment>
          <div className="steps-add-camera">
            <Steps size="small" current={1}>
              <Step title={t('Scan')} />
              <Step title={t('Activate')} />
              <Step title={t('Update')} />
            </Steps>
          </div>
          {autoScan.data.map((device, index) => (
            <div className="add-camera-step-2" key={device.serial}>
              <div className="add-camera-child">
                <div style={{ position: "absolute", top: '5px', left: "8px" }}>{`Camera-${index + 1}`}</div>
                <div style={{ position: "absolute", top: '30px', left: "8px", marginTop: '-5px' }}>{device?.source ?? ''}</div>
                <div className="thumbnail">
                  <img
                    className="img-thumbnail"
                    alt={device.name}
                    src={
                      device.thumbnail && device.thumbnail.match(/http/g)
                        ? device.thumbnail
                        : Thumbnail
                    }
                  />
                </div>
              </div>
              {device.authenticationRtsp === 'require' ? (
                <div className="add-camera-form">
                  <div className="title">
                    {t('Streaming requires authentication')}
                  </div>
                  <div className="main-form">
                    <Form
                      form={form}
                      name="nest-messages"
                      onFinish={(values) =>
                        handleAuthenticationSource({
                          username: values.username,
                          password: values.password,
                          serial: device.serial,
                          source: device.source,
                        })
                      }
                    >
                      <Form.Item
                        label={t('Username')}
                        name="username"
                        rules={[
                          {
                            required: true,
                            message: t('Please enter the username!'),
                          },
                          {
                            pattern: regex.isName,
                            message: t('Please enter only letters and numbers!'),
                          },
                          {
                            max: 80,
                            message: t('Please input up to 80 characters!'),
                          },
                        ]}
                      >
                        <Input placeholder={t('Username')} />
                      </Form.Item>
                      <Form.Item
                        label={t('Password')}
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: t('Please enter the password!'),
                          },
                          {
                            pattern: regex.isPassword,
                            message: t('Please enter the password!'),
                          },
                          {
                            max: 80,
                            message: t('Please input up to 80 characters!'),
                          },
                        ]}
                      >
                        <Input type="password" placeholder={t('Password')} />
                      </Form.Item>
                      <div className="btn-side">
                        <div className="child-btn-side">
                          <Form.Item>
                            <Button
                              onClick={() => {
                                form.resetFields();
                                setAutoScan({ loading: 0, data: [] });
                              }}
                            >
                              {t('Back')}
                            </Button>
                          </Form.Item>
                        </div>
                        <div className="child-btn-side">
                          <Form.Item>
                            <Button
                              type="primary"
                              loading={device.loading}
                              htmlType="submit"
                            >
                              {t('Validate')}
                            </Button>
                          </Form.Item>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              ) : !device.isAdded ? (
                <div className="btn-add-camera">
                  <Fragment>
                    <Popconfirm
                      title={t('Are you sure to add this camera?')}
                      onConfirm={() => handleAddExternalDevice(device.serial, device)}
                      okText={t('Accept')}
                      cancelText={t('Cancel')}
                    >
                      <Button type="primary" loading={device.isLoading}>
                        {t('Add device')}
                      </Button>
                    </Popconfirm>
                  </Fragment>
                </div>
              ) : (
                <div className="add-camera-form">
                  <div className="title">
                    Serial: <span>{device.serial}</span>
                  </div>
                  <div className="main-form">
                    <Form
                      form={form}
                      name="nest-messages"
                      onFinish={(values) =>
                        handleUpdateDevice({
                          name: values.name,
                          location: values.location,
                          serial: device.serial,
                          tags: values.tags?.join(';')?.toLowerCase() || ';',
                        })
                      }
                    >
                      <Form.Item
                        name="name"
                        rules={[
                          {
                            pattern: regex.isName,
                            message: t('Please enter only letters and numbers!'),
                          },
                          {
                            max: 80,
                            message: t('Please input up to 80 characters!'),
                          },
                        ]}
                        label={t('Name')}
                      >
                        <Input
                          placeholder={`${t('Default name')}: ${device.serial}`}
                        />
                      </Form.Item>
                      <Form.Item
                        name="location"
                        rules={[
                          {
                            pattern: regex.isName,
                            message: t('Please enter only letters and numbers!'),
                          },
                          {
                            max: 200,
                            message: t('Please input up to 200 characters!'),
                          },
                        ]}
                        label={t('Location')}
                      >
                        <LocationSearchLocationIQ
                          address={address.note}
                          clearAddress={() => setAddress({ note: '' })}
                          onChange={handleAddressChange}
                          onAddressSelect={handleSelect}
                          setIsBlock={setIsBlock}
                        />
                      </Form.Item>
                      <Form.Item
                        name="tags"
                        className="tags-device"
                        label={t('Tag')}
                        style={{ borer: '1px solid #434343', borderRadius: '2px' }}
                      >
                        <InputTag numberTag={3} nameAdd="Add tag" />
                      </Form.Item>

                      <div className="btn-side">
                        <div className="child-btn-side">
                          <Form.Item>
                            <Button
                              onClick={() => handleFinishAdd(device.serial)}
                            >
                              {t('Skip')}
                            </Button>
                          </Form.Item>
                        </div>
                        <div className="child-btn-side">
                          <Form.Item>
                            <Button
                              type="primary"
                              loading={device.loading}
                              htmlType="submit"
                            >
                              {t('Update')}
                            </Button>
                          </Form.Item>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              )}
            </div>
          ))}
          {/* </div> */}
        </Fragment>
      ) : (
        <Fragment>
          <div className="steps-add-camera">
            <Steps size="small" current={0}>
              <Step title={t('Scan')} />
              <Step title={t('Activate')} />
              <Step title={t('Update')} />
            </Steps>
          </div>
          <div className="guidelines-add-camera">
            <div className="title">
              {t('The function supports the standard ONVIF cameras')}
            </div>
            <ul>
              <li>
                {t(
                  'Require gateway to be connected to the same network as the camera'
                )}
              </li>
              <li>
                {t('Please prepare your login information to the camera')}
              </li>
            </ul>
          </div>
          <Result
            icon={
              autoScan && autoScan.loading === 0 ? (
                <SyncOutlined />
              ) : autoScan.loading === 1 ? (
                <SyncOutlined spin />
              ) : (
                <WarningOutlined style={{ color: colorsDark.$warning }} />
              )
            }
            title={t(
              autoScan.loading === -1
                ? 'Device not found, scan again!'
                : 'Scan devices via your Gateway!'
            )}
            extra={
              <Button
                type="primary"
                onClick={() => handleOnScan()}
                disabled={autoScan.loading === 1}
              >
                {t('Scan Now')}
              </Button>
            }
          />
        </Fragment>
      )}
    </div>
  );
};

export default ScanAddCameraComponent;
